import {chain, compact, omit} from 'lodash';
import {DateTime} from 'luxon';
import {Optional} from 'utility-types';

import {
  collectRecordEntries,
  isDefined,
} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  isSearchModifierToken,
  splitSearchQuery,
} from '../../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {StartAndEndDates} from '../../../../../models/dates/dateIntervalModel';
import {parsePeriod} from '../../hints/searchHintDate/dateUtils';
import {maybeRemoveDoubleQuotes, maybeWrapWithDoubleQuotes} from './inboxesSearchQueryBuilderHelpers';

type FreeformModifiersEnum = ModifiersEnum.BODY | ModifiersEnum.SUBJECT;
type DateModifiersEnum = ModifiersEnum.AFTER | ModifiersEnum.BEFORE | ModifiersEnum.DURING;
type StoredModifiersEnum = Exclude<
  ModifiersEnum,
  FreeformModifiersEnum | DateModifiersEnum | ModifiersEnum.CUSTOM_FIELD
>;

interface CustomFieldFilter {
  name: string;
  value: string | undefined;
}

export class BuiltQuery {
  static emptyModifierValues: () => Record<StoredModifiersEnum, Set<string>> = () => ({
    [ModifiersEnum.ACCOUNT]: new Set(),
    [ModifiersEnum.ASSIGNEE]: new Set(),
    [ModifiersEnum.AUTHOR]: new Set(),
    [ModifiersEnum.BCC]: new Set(),
    [ModifiersEnum.BODY]: new Set(),
    [ModifiersEnum.CC]: new Set(),
    [ModifiersEnum.CHANNEL_TYPE]: new Set(),
    [ModifiersEnum.COMMENTER]: new Set(),
    [ModifiersEnum.FROM]: new Set(),
    [ModifiersEnum.HAS]: new Set(),
    [ModifiersEnum.ID]: new Set(),
    [ModifiersEnum.IN]: new Set(),
    [ModifiersEnum.INBOX]: new Set(),
    [ModifiersEnum.IS]: new Set(),
    [ModifiersEnum.MENTION]: new Set(),
    [ModifiersEnum.OBJECT]: new Set(),
    [ModifiersEnum.PARTICIPANT]: new Set(),
    [ModifiersEnum.RECIPIENT]: new Set(),
    [ModifiersEnum.SUBJECT]: new Set(),
    [ModifiersEnum.TAG]: new Set(),
    [ModifiersEnum.TO]: new Set(),
    [ModifiersEnum.TYPE]: new Set(),
    [ModifiersEnum.LINK]: new Set(),
    [ModifiersEnum.WITH]: new Set(),
  });

  private constructor(
    private readonly textTokens: string[],
    private readonly selectedDateRange: StartAndEndDates | undefined,
    private readonly modifierValues: Record<StoredModifiersEnum, Set<string>>,
    private readonly freeformModifierValues: Record<FreeformModifiersEnum, string>,
    private readonly customFieldFilter: CustomFieldFilter | undefined,
  ) {}

  static parse(query: string, userTimezone: string) {
    const {textTokens, rawModifierValues} = parseSearchQuery(query);
    const selectedDateRange = findSelectedDateRange(rawModifierValues, userTimezone);
    const freeformModifierValues = {
      [ModifiersEnum.BODY]: [...rawModifierValues[ModifiersEnum.BODY]].join(' '),
      [ModifiersEnum.SUBJECT]: [...rawModifierValues[ModifiersEnum.SUBJECT]].join(' '),
    };

    let customFieldModifierValue;
    if (rawModifierValues[ModifiersEnum.CUSTOM_FIELD].size === 1) {
      const valueWithoutDoubleQuotes = maybeRemoveDoubleQuotes(
        rawModifierValues[ModifiersEnum.CUSTOM_FIELD].values().next().value,
      );

      if (valueWithoutDoubleQuotes) {
        // Same regexp as the customFieldFilter in the backend.
        const parsedValue = /^\s*([^=]+?)\s*=\s*(.*)$/.exec(valueWithoutDoubleQuotes);
        if (parsedValue) {
          const [, customFieldName, customFieldValue] = parsedValue;
          customFieldModifierValue = {name: customFieldName, value: customFieldValue};
        }
      }
    }

    const modifierValues = omit(rawModifierValues, [
      ModifiersEnum.AFTER,
      ModifiersEnum.BEFORE,
      ModifiersEnum.DURING,
      ModifiersEnum.CUSTOM_FIELD,
      ModifiersEnum.BODY,
      ModifiersEnum.SUBJECT,
    ]);

    return new BuiltQuery(
      textTokens,
      selectedDateRange,
      modifierValues,
      freeformModifierValues,
      customFieldModifierValue,
    );
  }

  render() {
    const textTokensString = this.textTokens.join(' ');
    const modifierValuesWithDateModifiers = withDateModifiers(this.modifierValues, this.selectedDateRange);
    const modifierValuesWithFreeform: Optional<
      Record<ModifiersEnum, ReadonlySet<string> | readonly string[]>,
      ModifiersEnum.CUSTOM_FIELD
    > = {
      ...modifierValuesWithDateModifiers,
      [ModifiersEnum.BODY]: buildModifierValuesFromFreeform(this.freeformModifierValues[ModifiersEnum.BODY]),
      [ModifiersEnum.SUBJECT]: buildModifierValuesFromFreeform(
        this.freeformModifierValues[ModifiersEnum.SUBJECT],
      ),
    };

    if (this.customFieldFilter?.value) {
      modifierValuesWithFreeform[ModifiersEnum.CUSTOM_FIELD] = [
        maybeWrapWithDoubleQuotes(`${this.customFieldFilter.name}=${this.customFieldFilter.value}`),
      ];
    }

    const modifiersString = Object.entries(modifierValuesWithFreeform)
      .flatMap(([modifier, values]) => [...values].map((value) => `${modifier}:${value}`))
      .join(' ');

    return collapseWhitespacesAndLineBreaks(compact([textTokensString, modifiersString]).join(' '));
  }

  getModifierValues(modifier: StoredModifiersEnum) {
    return [...this.modifierValues[modifier]];
  }

  hasModifierValue(modifier: StoredModifiersEnum, value: string) {
    return this.modifierValues[modifier].has(value);
  }

  hasAnyModifierValue(modifier: StoredModifiersEnum | ModifiersEnum.CUSTOM_FIELD) {
    if (modifier === ModifiersEnum.CUSTOM_FIELD) {
      return isDefined(this.customFieldFilter?.value);
    }

    return this.modifierValues[modifier].size > 0;
  }

  getSelectedDateRange() {
    return this.selectedDateRange;
  }

  getFreeformModifierValue(modifier: FreeformModifiersEnum) {
    return this.freeformModifierValues[modifier];
  }

  getCustomFieldName(): string | undefined {
    return this.customFieldFilter?.name;
  }

  getCustomFieldValue(): string | undefined {
    return this.customFieldFilter?.value;
  }

  withModifierValues(modifier: ModifiersEnum, values: readonly string[] | ReadonlySet<string>) {
    return new BuiltQuery(
      this.textTokens,
      this.selectedDateRange,
      {
        ...this.modifierValues,
        [modifier]: new Set(values),
      },
      this.freeformModifierValues,
      this.customFieldFilter,
    );
  }

  withEmptyModifierValues(modifier: ModifiersEnum) {
    return new BuiltQuery(
      this.textTokens,
      this.selectedDateRange,
      {
        ...this.modifierValues,
        [modifier]: new Set(),
      },
      this.freeformModifierValues,
      this.customFieldFilter,
    );
  }

  withCollapsedSingleValue(modifier: StoredModifiersEnum) {
    // If we have one or zero values, nothing to change.
    if (this.modifierValues[modifier].size <= 1) {
      return this;
    }

    // Else, remove modifier values.
    return this.withEmptyModifierValues(modifier);
  }

  withModifierValueSetTo(modifier: StoredModifiersEnum, value: string, shouldBeActive: boolean) {
    const newValuesForModifier = new Set(this.modifierValues[modifier]);

    if (shouldBeActive) {
      newValuesForModifier.add(value);
    } else {
      newValuesForModifier.delete(value);
    }

    return new BuiltQuery(
      this.textTokens,
      this.selectedDateRange,
      {
        ...this.modifierValues,
        [modifier]: newValuesForModifier,
      },
      this.freeformModifierValues,
      this.customFieldFilter,
    );
  }

  withToggledModifierValue(modifier: StoredModifiersEnum, value: string) {
    return this.withModifierValueSetTo(modifier, value, !this.hasModifierValue(modifier, value));
  }

  withFreeformModifierValue(modifier: FreeformModifiersEnum, valueString: string) {
    return new BuiltQuery(
      this.textTokens,
      this.selectedDateRange,
      this.modifierValues,
      {
        ...this.freeformModifierValues,
        [modifier]: valueString,
      },
      this.customFieldFilter,
    );
  }

  withSelectedDateRange(selectedDateRange: StartAndEndDates | undefined) {
    return new BuiltQuery(
      this.textTokens,
      selectedDateRange,
      this.modifierValues,
      this.freeformModifierValues,
      this.customFieldFilter,
    );
  }

  withCustomFieldFilter(customFieldFilter: CustomFieldFilter | undefined) {
    return new BuiltQuery(
      this.textTokens,
      this.selectedDateRange,
      this.modifierValues,
      this.freeformModifierValues,
      customFieldFilter,
    );
  }

  computeChangedModifiers(other: BuiltQuery): readonly ModifiersEnum[] {
    const changedModifiers = [];
    const modifierValuesWithDate = withDateModifiers(this.modifierValues, this.selectedDateRange);
    const otherModifierValuesWithDate = withDateModifiers(other.modifierValues, other.selectedDateRange);

    for (const [modifier, values] of collectRecordEntries(modifierValuesWithDate)) {
      const otherValues = otherModifierValuesWithDate[modifier];
      if (otherValues.size !== values.size) {
        changedModifiers.push(modifier);
      } else {
        const haveSameValues = [...values].every((value) => otherValues.has(value));
        if (!haveSameValues) {
          changedModifiers.push(modifier);
        }
      }
    }

    for (const [modifier, value] of collectRecordEntries(this.freeformModifierValues)) {
      const otherValue = other.freeformModifierValues[modifier];
      if (otherValue !== value) {
        changedModifiers.push(modifier);
      }
    }

    if (
      this.customFieldFilter?.name !== other.customFieldFilter?.name ||
      this.customFieldFilter?.value !== other.customFieldFilter?.value
    ) {
      changedModifiers.push(ModifiersEnum.CUSTOM_FIELD);
    }

    return changedModifiers;
  }
}

function parseSearchQuery(query: string) {
  const modifierValues: Record<ModifiersEnum, Set<string>> = {
    [ModifiersEnum.ACCOUNT]: new Set(),
    [ModifiersEnum.AFTER]: new Set(),
    [ModifiersEnum.ASSIGNEE]: new Set(),
    [ModifiersEnum.AUTHOR]: new Set(),
    [ModifiersEnum.BCC]: new Set(),
    [ModifiersEnum.BEFORE]: new Set(),
    [ModifiersEnum.BODY]: new Set(),
    [ModifiersEnum.CC]: new Set(),
    [ModifiersEnum.CHANNEL_TYPE]: new Set(),
    [ModifiersEnum.COMMENTER]: new Set(),
    [ModifiersEnum.DURING]: new Set(),
    [ModifiersEnum.FROM]: new Set(),
    [ModifiersEnum.HAS]: new Set(),
    [ModifiersEnum.ID]: new Set(),
    [ModifiersEnum.IN]: new Set(),
    [ModifiersEnum.INBOX]: new Set(),
    [ModifiersEnum.IS]: new Set(),
    [ModifiersEnum.MENTION]: new Set(),
    [ModifiersEnum.OBJECT]: new Set(),
    [ModifiersEnum.PARTICIPANT]: new Set(),
    [ModifiersEnum.RECIPIENT]: new Set(),
    [ModifiersEnum.SUBJECT]: new Set(),
    [ModifiersEnum.TAG]: new Set(),
    [ModifiersEnum.CUSTOM_FIELD]: new Set(),
    [ModifiersEnum.TO]: new Set(),
    [ModifiersEnum.TYPE]: new Set(),
    [ModifiersEnum.LINK]: new Set(),
    [ModifiersEnum.WITH]: new Set(),
  };

  const textTokens = [];

  const queryParts = splitSearchQuery(query);

  for (const queryPart of queryParts) {
    if (queryPart.token?.value) {
      const tokenValue = queryPart.token.value;

      if (isSearchModifierToken(queryPart.token)) {
        const quoteAtBeginningOnly = tokenValue[0] === '"' && tokenValue[tokenValue.length - 1] !== '"';

        // This handles a difference between our frontend parser and our backend parser.
        // More specifically about how they handle unterminated quoted strings such as
        // `subject:"hello body:test`
        // In the backend, this will be interpreted as `subject:hello body:test`.
        // In the frontend, this will be rendered as `subject:"hello body:test"`.
        // This is useful in the context of the user writing their query and starting with the first quote.
        // Because we're trying to match the backend behavior we need this bandaid.
        // Longer term, we should use the backend implementation.
        if (quoteAtBeginningOnly) {
          const [modifierPart, ...otherParts] = tokenValue.split(/\s+/);

          if (modifierPart) {
            modifierValues[queryPart.token.modifier].add(modifierPart);
          }

          textTokens.push(...otherParts);
        } else {
          modifierValues[queryPart.token.modifier].add(tokenValue);
        }
      } else {
        textTokens.push(tokenValue);
      }
    }
  }

  return {textTokens, rawModifierValues: modifierValues};
}

function findSelectedDateRange(
  dateModifierValues: Record<DateModifiersEnum, Set<string>>,
  userTimezone: string,
) {
  const duringModifierValues = dateModifierValues[ModifiersEnum.DURING];
  const parsedDuringValues = chain([...duringModifierValues])
    .map((value) => parsePeriod(value, userTimezone, false))
    .compact()
    .value();

  const afterModifierValues = dateModifierValues[ModifiersEnum.AFTER];
  const effectiveAfterValue: number | undefined = chain([...afterModifierValues])
    .map((value) => parsePeriod(value, userTimezone, true))
    .compact()
    .concat(parsedDuringValues)
    .map((period) => period.start)
    .max()
    .value();

  const beforeModifierValues = dateModifierValues[ModifiersEnum.BEFORE];
  const effectiveBeforeValue: number | undefined = chain([...beforeModifierValues])
    .map((value) => parsePeriod(value, userTimezone, true))
    .compact()
    .concat(parsedDuringValues)
    .map((period) => period.end)
    .min()
    .value();

  if (!effectiveAfterValue || !effectiveBeforeValue || effectiveAfterValue < effectiveBeforeValue) {
    const selectedDateRange: StartAndEndDates = {
      startDate: isDefined(effectiveAfterValue)
        ? DateTime.fromMillis(effectiveAfterValue, {zone: userTimezone})
        : undefined,
      endDate: isDefined(effectiveBeforeValue)
        ? DateTime.fromMillis(effectiveBeforeValue, {zone: userTimezone})
        : undefined,
    };

    if (selectedDateRange.startDate || selectedDateRange.endDate) {
      return selectedDateRange;
    }
  }

  return undefined;
}

function withDateModifiers(
  modifierValues: Record<StoredModifiersEnum, Set<string>>,
  selectedDateRange: StartAndEndDates | undefined,
): Record<StoredModifiersEnum | DateModifiersEnum, Set<string>> {
  const modifierValuesWithDate = {
    ...modifierValues,
    [ModifiersEnum.AFTER]: new Set<string>(),
    [ModifiersEnum.BEFORE]: new Set<string>(),
    [ModifiersEnum.DURING]: new Set<string>(),
  };

  const startAsString = selectedDateRange?.startDate?.toFormat('yyyy-MM-dd');
  const endAsString = selectedDateRange?.endDate?.toFormat('yyyy-MM-dd');

  if (startAsString && endAsString && startAsString === endAsString) {
    modifierValuesWithDate[ModifiersEnum.DURING].add(startAsString);
  } else {
    if (startAsString) {
      modifierValuesWithDate[ModifiersEnum.AFTER].add(startAsString);
    }

    if (endAsString) {
      modifierValuesWithDate[ModifiersEnum.BEFORE].add(endAsString);
    }
  }

  return modifierValuesWithDate;
}

function buildModifierValuesFromFreeform(valueString: string) {
  const trimmedValueString = valueString.trim();

  // We're using splitSearchQuery for it's ability to extract double-quoted sequences.
  const valueStringParts = trimmedValueString ? splitSearchQuery(trimmedValueString) : [];
  const normalKeywords: string[] = [];
  const modifierValues: string[] = [];

  for (const part of valueStringParts) {
    const {token} = part;
    if (token?.value) {
      if ('modifier' in token) {
        // If the splitter detected a modifier, use as regular token.
        normalKeywords.push(token.modifier);
      }

      if (token.value[0] === '"' && token.value[token.value.length - 1] === '"') {
        // We don't need to remove quotes from token.value because the regexp
        // matches double-quoted content non-greedily.
        modifierValues.push(token.value);
      } else {
        // Make sure token.value does not contain stray spaces or quotes.
        normalKeywords.push(...token.value.split(/[\s"]+/));
      }
    }
  }

  if (normalKeywords.length > 0) {
    modifierValues.push(normalKeywords.join(';'));
  }

  return modifierValues;
}

function collapseWhitespacesAndLineBreaks(text: string): string {
  return text.trim().replace(/\s+/gi, ' ');
}
